import React from 'react';
import { NextPage } from 'next';
import {
  AutoCompleteV2, ICON_MAGNIFY, SpacingName, Spacing, Typography, FontWeight, Button, ButtonColor,
} from '@doveit/lego';
import { capitalizeWords } from '@doveit/hammer';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { requireFromStaticAsset, requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import {
  FRANCHISING_AS, FRANCHISING_URL, JOIN_OUR_AGENTS_AS, JOIN_OUR_AGENTS_URL, buildAgencyByLocalityURLObject, buildSellPropertyURLObject, goToPage,
} from '../client/utils/links';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import PageSection from '../client/components/page-section/PageSection';
import CityList from '../client/components/city-list/CityList';
import { City, HeroSection } from '../client/types';
import * as styles from '../client/templates/agencies/Agencies.style';
import { getCities } from '../services/cities/cities';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import Hero from '../client/templates/common/hero/Hero';
import BasicStripe, { CTAWithOnCLick } from '../client/components/basic-stripe/BasicStripe';
import Stripe from '../client/slices/stripe/Stripe';

export interface AgenciesProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection,
  introSection: HeroSection,
  sellProperty: {
    title: string,
    subtitle: string,
    description: string,
    image: {
      url: string,
      alt: string,
    },
    cta: CTAWithOnCLick,
  },
  citiesSection: {
    title: string,
  },
  join: {
    title: string,
  },
  franchising: {
    title: string,
    subtitle: string,
    description: string,
    image: {
      url: string,
      alt: string,
    },
    cta: CTAWithOnCLick,
  },
  joinUs: {
    title: string,
    subtitle: string,
    description: string,
    image: {
      url: string,
      alt: string,
    },
    cta: CTAWithOnCLick,
  },
  cities: City[],
}

export const Agencies: NextPage<AgenciesProps> = ({
  SEO,
  hero,
  introSection,
  sellProperty,
  citiesSection,
  cities,
  join,
  franchising,
  joinUs,
}) => {
  const breadcrumb = React.useMemo(() => ([{
    label: 'Agenzie immobiliari',
  }]), []);

  const searchCity = React.useCallback(
    (query: string) => Promise.resolve(cities.filter((city) => city.locality.toLocaleLowerCase().startsWith(query.toLocaleLowerCase()))),
    [cities],
  );

  const printSuggestion = React.useCallback((city: City) => `Provincia di ${capitalizeWords(city.locality)}`, []);

  const onProvinceSelected = React.useCallback((city?: City) => {
    if (city) {
      goToPage(buildAgencyByLocalityURLObject({ locality: city.locality }));
    }
  }, []);

  const goToFranchisingPage = React.useCallback(() => {
    goToPage({
      url: FRANCHISING_URL,
      as: FRANCHISING_AS,
    });
  }, []);

  const goToJoinOurAgentsPage = React.useCallback(() => {
    goToPage({
      url: JOIN_OUR_AGENTS_URL,
      as: JOIN_OUR_AGENTS_AS,
    });
  }, []);

  const goToSellPropertyPage = React.useCallback(() => {
    goToPage(buildSellPropertyURLObject());
  }, []);

  return (
    <AppLayout>
      <PageHead SEO={SEO} />
      <PageLayout>
        <Hero
          title={hero.title}
          description={hero.description}
          backgroundImage={hero.backgroundImage}
          primarySlot={(
            <div css={styles.searchAgency}>
              <AutoCompleteV2
                suggestions={searchCity}
                printSuggestion={printSuggestion}
                icon={ICON_MAGNIFY}
                onSuggestionSelected={onProvinceSelected}
                placeholder="Es. Roma"
              />
            </div>
          )}
        />

        <PageLayout.Content addCss={styles.content}>
          <PageLayout.Main>
            <PageSection>
              <PageSection.Wrapper>
                <Typography.H4 fontWeight={FontWeight.LIGHT}>
                  {introSection.description}
                </Typography.H4>
              </PageSection.Wrapper>
            </PageSection>
            <Stripe
              imageURL={requireFromStaticAsset(sellProperty.image.url)}
              imageAlt={sellProperty.image.alt}
            >
              <Typography.H2
                color="brand.primary"
                uppercase
              >
                {sellProperty.title}
              </Typography.H2>
              <Spacing margin={[SpacingName.SMALL, 0]}>
                <Typography.H3
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {sellProperty.subtitle}
                </Typography.H3>
              </Spacing>
              <Spacing margin={[SpacingName.SMALL, 0]}>
                <Typography.H4
                  tagName="p"
                  fontWeight={FontWeight.LIGHT}
                >
                  {sellProperty.description}
                </Typography.H4>
              </Spacing>
              <Button
                {...sellProperty.cta}
                color={ButtonColor.PRIMARY}
                onClick={goToSellPropertyPage}
              />
            </Stripe>
            <PageSection padding={[SpacingName.BIG, 0, 0]}>
              <PageSection.Wrapper>
                <PageSection.Title>
                  {citiesSection.title}
                </PageSection.Title>
                <PageSection.Block>
                  <CityList
                    columns={3}
                    cities={cities.map((item) => ({
                      name: item.title,
                      picture: requireFromStaticAsset(item.smallPicture),
                      title: `Agenzia immobiliare ${item.title}`,
                      urlObject: buildAgencyByLocalityURLObject({ locality: item.locality }),
                    }))}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title>
                  {join.title}
                </PageSection.Title>
                <PageSection.Block>
                  <BasicStripe
                    title={franchising.title}
                    subtitle={franchising.subtitle}
                    description={franchising.description}
                    image={{
                      url: franchising.image.url,
                      alt: franchising.image.alt,
                    }}
                    cta={{
                      ...franchising.cta,
                      onClick: goToFranchisingPage,
                    }}
                  />
                </PageSection.Block>
                <PageSection.Block>
                  <BasicStripe
                    title={joinUs.title}
                    subtitle={joinUs.subtitle}
                    description={joinUs.description}
                    image={{
                      url: joinUs.image.url,
                      alt: joinUs.image.alt,
                    }}
                    cta={{
                      ...joinUs.cta,
                      onClick: goToJoinOurAgentsPage,
                    }}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>
          </PageLayout.Main>
        </PageLayout.Content>
        <PageSection.Wrapper>
          <PageLayout.Breadcrumb items={breadcrumb} />
        </PageSection.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<AgenciesProps>(async () => {
  const [common, data, cities] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/agencies.yml'),
    getCities(),
  ]);

  return {
    props: {
      ...common,
      ...data,
      hero: {
        ...data.hero,
        backgroundImage: requireResourcesFromStaticAssets(data.hero.backgroundImage),
      },
      cities,
    },
  };
});

export default withError(Agencies);
